import { Suspense, lazy } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import Layout from './components/Layout';
import { useAuth } from './context/AuthContext';
import { useI18n } from './context/I18nContext';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Incidents = lazy(() => import('./pages/Incidents'));
const MonitorDetail = lazy(() => import('./pages/MonitorDetail'));
const StatusPageConfig = lazy(() => import('./pages/StatusPageConfig'));
const StatusPageIncidents = lazy(() => import('./pages/StatusPageIncidents'));
const PublicStatusPage = lazy(() => import('./pages/PublicStatusPage'));
const PublicMonitorDetail = lazy(() => import('./pages/PublicMonitorDetail'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));

function FullScreenLoader() {
    const { t } = useI18n();

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 text-sm text-gray-500">
            {t('app.loading')}
        </div>
    );
}

function ProtectedRoute() {
    const { user, loading } = useAuth();

    if (loading) {
        return <FullScreenLoader />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return <Outlet />;
}

function PublicOnlyRoute() {
    const { user, loading } = useAuth();

    if (loading) {
        return <FullScreenLoader />;
    }

    if (user) {
        return <Navigate to="/" replace />;
    }

    return <Outlet />;
}

function App() {
    const { registrationEnabled } = useAuth();

    return (
        <Suspense fallback={<FullScreenLoader />}>
            <Routes>
                <Route element={<ProtectedRoute />}>
                    <Route path="/" element={<Layout />}>
                        <Route index element={<Dashboard />} />
                        <Route path="incidents" element={<Incidents />} />
                        <Route path="monitors/:id" element={<MonitorDetail />} />
                        <Route path="status-pages" element={<StatusPageConfig />} />
                        <Route path="status-pages/:id/incidents" element={<StatusPageIncidents />} />
                    </Route>
                </Route>

                <Route element={<PublicOnlyRoute />}>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={registrationEnabled ? <Register /> : <Navigate to="/login" replace />} />
                </Route>

                <Route path="/status/:slug" element={<PublicStatusPage />} />
                <Route path="/status/:slug/monitors/:monitorId" element={<PublicMonitorDetail />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

export default App;
